<template>
  <div class="full-container" ref="scroll">
    <!-- List -->
    <div class="list-container" ref="listContainer">
      <CommentSingle
        v-for="comment in comments"
        :key="comment.id"
        :post="post"
        :model="model"
        :data="comment"
        @delete="deleteCommentHandler"
        @reply="replyHandler"
        @changeHeight="raiseChangeListHeight"
      />
    </div>

    <!-- Error -->
    <div v-if="errorFetching">
      <p>
        {{ $t("error") }}
      </p>
      <button style="width: 100%" type="button" @click="loadComments">
        {{ $t("retry") }}
      </button>
    </div>
    <!-- Loading -->
    <div class="spinner" v-else-if="waitFetch">
      <Spinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import resources from "@/utils/resources";
import throttle from "lodash/throttle";

import { Post } from "~/types/Post";
import { Model } from "~/types/Model";
import { Comment } from "~/types/Comment";

const $emit = defineEmits<{
  (e: 'reply', data: { comment: Comment; cb: Function; }): void,
  (e: 'changeHeight', height: number): void,
}>();

// Props
const { post, model } = defineProps<{
  post: Post;
  model: Model;
}>();

// Data
const comments = ref<Comment[]>([]);
const excludedComments = ref<Comment[]>([]);
const errorFetching = ref(false);
const waitFetch = ref(false);
let nextPage = null;

// Refs
const listContainer = ref<HTMLElement | null>(null);

function appendComment(comment: Comment) {
  excludedComments.value.push(comment.id);
  comments.value.unshift(comment);

  raiseChangeListHeight();
}

function loadComments() {
  waitFetch.value = true;
  errorFetching.value = false;

  let fetchRequest = null;

  if (nextPage) {
    fetchRequest = resources.comments.getCommentsByPaginatorLink(nextPage);
  } else {
    fetchRequest = resources.comments.getComments(post.post_id);
  }

  nextPage = null;

  fetchRequest
    .then((response) => {
      let data: Comment[] = response.data;
      const meta = response.meta;

      if (excludedComments.value.length) {
        data = data.filter(
          (comment) => !excludedComments.value.includes(comment.id)
        );
      }

      comments.value = comments.value.concat(data);
      raiseChangeListHeight();

      if (!meta || !meta.pagination) return;
      const pagination = meta.pagination;

      if (pagination.current_page < meta.pagination.total_pages && meta.pagination.links) {
        nextPage = meta.pagination.links.next;
      }
    })
    .catch((error) => {
      console.log(error);
      errorFetching.value = true;
    })
    .finally(() => {
      waitFetch.value = false;
    });
}

function deleteCommentHandler(commentId: number) {
  const filterComment = (commentList: Comment[]) => {
    return commentList.filter((comment) => comment.id !== commentId);
  };
  
  excludedComments.value = filterComment(excludedComments.value);
  comments.value = filterComment(comments.value);
}

function replyHandler(data: any) {
  $emit("reply", data);
}

function raiseChangeListHeight() {
  nextTick(() => {
    $emit("changeHeight", listContainer.value!.clientHeight);
  });
}

function endOfScrollReached() {
  throttle(() => {
    if (nextPage) loadComments();
  }, 500)();
};

onMounted(() => {
  loadComments();
});

defineExpose({
  appendComment,
  endOfScrollReached,
});
</script>

<style scoped lang="scss">
.full-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: fit-content;
  margin: 0;
  padding: 0;
}

.list-container {
  display: flex;
  width: 100%;
  height: fit-content;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding-block: 0;
  padding-inline: 1em;
}

.spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>
